import React, { FC } from 'react';
import {
  PauseOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  CheckOutlined,
  WarningOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { UseAudioRecorderReturn, formatAudioDuration } from './useAudioRecorder';

export interface AudioRecorderInlineProps {
  recorder: UseAudioRecorderReturn;
  maxDurationSeconds?: number;
  onFinish?: () => void;
  onCancel?: () => void;
  className?: string;
}

export const AudioRecorderInline: FC<AudioRecorderInlineProps> = ({
  recorder,
  maxDurationSeconds = 300,
  onFinish,
  onCancel,
  className = '',
}) => {
  const {
    isPaused,
    duration,
    volumeLevel,
    canStop,
    permissionStatus,
    errorMessage,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  } = recorder;

  const handleCancel = () => {
    cancelRecording();
    onCancel?.();
  };

  const handleStop = () => {
    if (!canStop) return;
    stopRecording();
    onFinish?.();
  };

  // If permission is blocked or device not found
  if (
    permissionStatus === 'denied' ||
    permissionStatus === 'not_found' ||
    permissionStatus === 'unsupported'
  ) {
    return (
      <div className={`audio-recorder-inline error-state ${className}`}>
        <div className="error-message-box">
          <WarningOutlined className="error-icon" />
          <span className="error-text">
            {errorMessage ||
              'Microphone access is blocked. Please allow permissions in browser settings.'}
          </span>
        </div>
        <button
          type="button"
          className="recorder-cancel-btn"
          onClick={handleCancel}
          title="Dismiss"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // Equalizer visualizer bars (12 bars scaled by volume level)
  const equalizerBars = Array.from({ length: 14 }).map((_, index) => {
    // Generate organic wave based on volume and index
    const baseHeight = 15;
    const waveFactor = Math.sin((index / 14) * Math.PI);
    const dynamicHeight = Math.max(
      baseHeight,
      Math.min(100, volumeLevel * waveFactor * 1.4 + (isPaused ? 0 : 10)),
    );
    return isPaused ? baseHeight : dynamicHeight;
  });

  return (
    <div className={`audio-recorder-inline ${className}`}>
      {/* Left section: Pulsing dot & Timer */}
      <div className="recording-status-section">
        <div className={`recording-dot ${isPaused ? 'paused' : 'live'}`} />
        <span className="recording-text">{isPaused ? 'Paused' : 'Recording'}</span>
        <span className="recording-timer">
          {formatAudioDuration(Math.min(duration, maxDurationSeconds))}
          <span className="max-timer"> / {formatAudioDuration(maxDurationSeconds)}</span>
        </span>
      </div>

      {/* Center section: Live audio visualizer (hidden when paused) */}
      {!isPaused && (
        <div className="recording-equalizer-container">
          <div className="equalizer-bars">
            {equalizerBars.map((heightPercent, idx) => (
              <span key={idx} className="equalizer-bar" style={{ height: `${heightPercent}%` }} />
            ))}
          </div>
        </div>
      )}

      {/* Right section: Control action buttons */}
      <div className="recording-actions-section">
        {/* Discard / Trash button */}
        <button
          type="button"
          className="action-btn discard-btn"
          onClick={handleCancel}
          title="Discard voice note"
        >
          <DeleteOutlined />
        </button>

        {/* Pause / Resume toggle button */}
        <button
          type="button"
          className="action-btn pause-resume-btn"
          onClick={isPaused ? resumeRecording : pauseRecording}
          title={isPaused ? 'Resume recording' : 'Pause recording'}
        >
          {isPaused ? <CaretRightOutlined /> : <PauseOutlined />}
        </button>

        {/* Stop / Done button (with 2-second debounce protection) */}
        <button
          type="button"
          className={`action-btn finish-btn ${!canStop ? 'disabled-debouncing' : ''}`}
          onClick={handleStop}
          disabled={!canStop}
          title={!canStop ? 'Recording (minimum 2s)...' : 'Finish recording'}
        >
          {!canStop ? <LoadingOutlined className="debounce-spin" /> : <CheckOutlined />}
        </button>
      </div>
    </div>
  );
};
